import React, { useContext, useEffect, useState, useMemo } from 'react'
import { mapData, converDate } from '@/product/common/components'
import PrimaryButton from '@/product/components/atoms/PrimaryButton'
import { Button } from '@material-ui/core'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CustomDialog from '../../organisms/Dialog/index'

import StripeCardContext from '@/product/context/stripe/card/cardContext'
import AuthContext from '@/product/context/auth/authContext'
import ProductCommonContext from '@/product/context/common/commonContext'
import AlertContext from '@/product/context/alert/alertContext'
import Popup from '../../organisms/Popup'
import Loaders from '../../molecules/Loaders'
import { loadStripe } from '@stripe/stripe-js'
import {
    Elements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    AddressElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js'

const ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: "'Poppins', 'Helvetica Neue', Helvetica, sans-serif",
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
    },
}

// Stripe appearance to match MUI outlined inputs
const STRIPE_APPEARANCE = {
    theme: 'none',
    variables: {
        colorPrimary: '#333333',
        colorBackground: '#ffffff',
        colorText: '#32325d',
        colorDanger: '#fa755a',
        fontFamily: "'Poppins', 'Helvetica Neue', Helvetica, sans-serif",
        borderRadius: '4px',
        fontSizeBase: '16px',
    },
    rules: {
        '.Input': {
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            padding: '18.5px 14px',
            fontSize: '16px',
            color: '#32325d',
            backgroundColor: '#ffffff',
            boxShadow: 'none',
        },
        '.Input:focus': {
            border: '1px solid #333333',
            boxShadow: 'none',
        },
        '.Input--invalid': {
            border: '1px solid #fa755a',
        },
        '.Label': {
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 0.54)',
            fontWeight: '400',
        },
        '.Error': {
            color: '#fa755a',
            fontSize: '12px',
        },
    },
}

// ─── Inner form (must be inside <Elements>) ──────────────────────────
function StripeCardForm(props) {
    const { user } = useContext(AuthContext)
    const { createStripeCardWithPaymentMethod, responseStatus, clearResponse } =
        useContext(StripeCardContext)

    const stripe = useStripe()
    const elements = useElements()

    const [disabled, setDisabled] = useState(false)
    const [cardError, setCardError] = useState(null)
    const [addressData, setAddressData] = useState(null)
    const [addressComplete, setAddressComplete] = useState(false)

    const cardFormik = useFormik({
        initialValues: { primary_card: false },
        onSubmit: async (values) => {
            if (!stripe || !elements) return

            if (!addressComplete || !addressData) {
                setCardError('Please complete the billing address.')
                return
            }

            setDisabled(true)
            setCardError(null)

            const cardNumberElement = elements.getElement(CardNumberElement)
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: addressData.name || '',
                    address: {
                        line1: addressData.address?.line1 || '',
                        line2: addressData.address?.line2 || '',
                        city: addressData.address?.city || '',
                        state: addressData.address?.state || '',
                        postal_code: addressData.address?.postal_code || '',
                        country: addressData.address?.country || '',
                    },
                },
            })

            if (error) {
                setCardError(error.message)
                setDisabled(false)
                return
            }

            if (user) {
                createStripeCardWithPaymentMethod({
                    payment_method_id: paymentMethod.id,
                    primary_card: values.primary_card,
                })
            }
        },
    })

    useEffect(() => {
        if (responseStatus) {
            if (responseStatus.from === 'card_source_create') {
                if (responseStatus.status === 'success') {
                    cardFormik.resetForm()
                    setAddressData(null)
                    setAddressComplete(false)
                    const cardNumberElement = elements?.getElement(CardNumberElement)
                    const cardExpiryElement = elements?.getElement(CardExpiryElement)
                    const cardCvcElement = elements?.getElement(CardCvcElement)
                    if (cardNumberElement) cardNumberElement.clear()
                    if (cardExpiryElement) cardExpiryElement.clear()
                    if (cardCvcElement) cardCvcElement.clear()
                    clearResponse()
                    props.function(false, 'new', null)
                }
            }
            setDisabled(false)
            clearResponse()
        }
    }, [responseStatus])

    const defaultCardField = {
        formik: cardFormik,
        data: [
            {
                label: 'Set as default card',
                class: 'col-12',
                type: 'checkbox',
                name: 'primary_card',
            },
        ],
    }

    return (
        <Popup
            open={props.data.popup && props.data.status === 'new'}
            handleClose={() => props.function(false, 'new', null)}
            modaltitle="Add Card Details"
            className="cardDetailsPopup"
        >
            <div className="cardDetails stripeCardForm">
                <form onSubmit={cardFormik.handleSubmit} autoComplete="nofill">
                    <div className="stripeCardGrid">
                        <div className="stripeField stripeField--full">
                            <label>Card Number</label>
                            <div className="stripeElementWrapper">
                                <CardNumberElement
                                    options={ELEMENT_OPTIONS}
                                    onChange={(e) => setCardError(e.error ? e.error.message : null)}
                                />
                            </div>
                        </div>

                        <div className="stripeField stripeField--half">
                            <label>Expiry Month / Year</label>
                            <div className="stripeElementWrapper">
                                <CardExpiryElement
                                    options={ELEMENT_OPTIONS}
                                    onChange={(e) => setCardError(e.error ? e.error.message : null)}
                                />
                            </div>
                        </div>

                        <div className="stripeField stripeField--half">
                            <label>CVV</label>
                            <div className="stripeElementWrapper">
                                <CardCvcElement
                                    options={ELEMENT_OPTIONS}
                                    onChange={(e) => setCardError(e.error ? e.error.message : null)}
                                />
                            </div>
                        </div>

                        {cardError && (
                            <div className="stripeField stripeField--full">
                                <div
                                    className="card-errors"
                                    role="alert"
                                    style={{ color: '#fa755a', marginBottom: '10px' }}
                                >
                                    {cardError}
                                </div>
                            </div>
                        )}

                        <div className="stripeField stripeField--full">
                            <AddressElement
                                options={{
                                    mode: 'billing',
                                    fields: { phone: 'never' },
                                    defaultValues: {
                                        address: { country: global.defaultCountry || 'US' },
                                    },
                                }}
                                onChange={(event) => {
                                    setAddressData(event.value)
                                    setAddressComplete(event.complete)
                                }}
                            />
                        </div>

                        <div className="stripeField stripeField--half customCheckbox">
                            {mapData(defaultCardField)}
                        </div>
                    </div>
                    <PrimaryButton
                        id="cardDetails_submit"
                        disabled={disabled || !stripe}
                        btnSize="large"
                        type="submit"
                        label={disabled ? 'Processing...' : 'Save'}
                    />
                </form>
            </div>
        </Popup>
    )
}

// ─── Main wrapper ─────────────────────────────────────────────────────
function CardPaymentMethodDetails(props) {
    const alertContext = useContext(AlertContext)
    const { setAlert } = alertContext
    const {
        get_all_payment_method_card_details,
        deleteStripeCard,
        updateStripeCard,
        defaultStripePaymentMethodCard,
        responseStatus,
        clearResponse,
    } = useContext(StripeCardContext)

    const [disabled, setDisabled] = useState(false)
    const [changeStatus, setChangeStatus] = useState(false)
    const changeChangeStatus = () => setChangeStatus(!changeStatus)

    const [reload, setReload] = useState(false)
    const creditCardYearOptions = []
    let date = new Date()
    let currentYear = date.getFullYear()
    let upToYear = parseInt(currentYear) + 25
    for (let year = parseInt(currentYear); year < parseInt(upToYear); year++) {
        creditCardYearOptions.push({ value: year, show: year })
    }
    const creditCardMonthOptions = []
    for (let month = 1; month <= 12; month++) {
        let monthValue = ('0' + month).slice(-2)
        creditCardMonthOptions.push({ value: monthValue, show: monthValue })
    }

    const validationupdateCard = Yup.object({
        exp_month: Yup.string().required('Required!'),
        exp_year: Yup.string().required('Required!'),
    })

    const updateCardformik = useFormik({
        initialValues: { source_id: '', exp_month: '', exp_year: '' },
        validationSchema: validationupdateCard,
        onSubmit: (values) => {
            updateStripeCard(values)
        },
    })

    const formikSelection = useFormik({
        initialValues: { data: '', status: '' },
        onSubmit: (values) => {
            if (values.status === 'delete') {
                deleteStripeCard({ source_id: props.data.data.id })
                setChangeStatus(false)
                props.function(false, 'new', null)
            } else if (values.status === 'default') {
                defaultStripePaymentMethodCard({
                    source_id: props.data.data.id,
                })
                setChangeStatus(false)
                props.function(false, 'new', null)
            }
        },
    })

    useEffect(() => {
        if (props.data.data) {
            if (props.data.status === 'delete') {
                formikSelection.values.data = props.data.data
                formikSelection.values.status = props.data.status
                console.log(
                    get_all_payment_method_card_details,
                    'gggggggg',
                    props.data.data,
                    formikSelection,
                )
                if (get_all_payment_method_card_details?.records?.length > 1) {
                    setChangeStatus(true)
                } else {
                    setAlert('Default card is required', 'error')
                    setChangeStatus(false)
                    props.function(false, 'new', null)
                }
            } else if (props.data.status === 'default') {
                formikSelection.values.data = props.data.data
                formikSelection.values.status = props.data.status
                setChangeStatus(true)
            } else if (props.data.status === 'update') {
                updateCardformik.values.source_id = props.data.data.id
                updateCardformik.values.exp_month = ('0' + props.data.data.card?.exp_month).slice(
                    -2,
                )
                updateCardformik.values.exp_year = props.data.data.card?.exp_year
                setReload(!reload)
            }
        }
    }, [props.data.data])

    useEffect(() => {
        if (responseStatus) {
            if (
                responseStatus.from === 'card_source_update' ||
                responseStatus.from === 'card_source_delete'
            ) {
                if (responseStatus.status === 'success') {
                    clearResponse()
                    props.function(false, 'new', null)
                }
            }
            setDisabled(false)
        }
    }, [responseStatus])

    const cardUpdate = {
        formik: updateCardformik,
        data: [
            {
                label: 'Expiry Month',
                placeholder: 'MM',
                class: 'col-6',
                type: 'select',
                name: 'exp_month',
                options: creditCardMonthOptions,
            },
            {
                label: 'Expiry Year',
                placeholder: 'YYYY',
                class: 'col-6',
                type: 'select',
                name: 'exp_year',
                options: creditCardYearOptions,
            },
        ],
    }

    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    const stripePromise = useMemo(() => {
        if (stripePublishableKey) return loadStripe(stripePublishableKey)
        return null
    }, [stripePublishableKey])

    return (
        <>
            {stripePromise ? (
                <Elements stripe={stripePromise} options={{ appearance: STRIPE_APPEARANCE }}>
                    <StripeCardForm {...props} />
                </Elements>
            ) : (
                <Popup
                    open={props.data.popup && props.data.status === 'new'}
                    size="md"
                    handleClose={() => props.function(false, 'new', null)}
                    modaltitle="Add Card Details"
                >
                    <div className="cardDetails">
                        <p style={{ color: 'red' }}>
                            Stripe is not configured. Please set REACT_APP_STRIPE_PUBLIC_KEY.
                        </p>
                    </div>
                </Popup>
            )}

            <CustomDialog title={'Confirmation'} open={changeStatus} function={changeChangeStatus}>
                <h5>
                    {formikSelection.values.status === 'delete'
                        ? 'Are you sure you want to delete'
                        : 'Are you sure you want to change the status'}
                </h5>
                <div className="actionWrapper">
                    <Button id="cardDetails_cancel" onClick={() => changeChangeStatus()}>
                        Cancel
                    </Button>
                    <form onSubmit={formikSelection.handleSubmit} autoComplete="nofill">
                        <PrimaryButton id="cardDetails_confirm" type="submit" label="Confirm" />
                    </form>
                </div>
            </CustomDialog>

            <Popup
                open={props.data.popup && props.data.status === 'update'}
                handleClose={() => props.function(false, 'update', null)}
                size="md"
                modaltitle="Update Card Details"
            >
                <form onSubmit={updateCardformik.handleSubmit} autoComplete="nofill">
                    <div className="row">
                        {mapData(cardUpdate)}
                        <div className="col-12">
                            <PrimaryButton
                                id="cardDetails_save"
                                disabled={disabled}
                                btnSize="large"
                                type="submit"
                                label={'Save'}
                            />
                        </div>
                    </div>
                </form>
            </Popup>
        </>
    )
}

export default CardPaymentMethodDetails
